import React, { memo, useRef, useState, useCallback, useEffect } from 'react';
import { Camera, Volume2, VolumeX } from 'lucide-react';
import { extractVideoFrame } from '@/utils/videoFrameExtractor';
import { toast } from 'sonner';

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const VideoProgressBar: React.FC<{
  nodeId?: string;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume?: number;
  isMuted?: boolean;
  hasAudioTrack?: boolean;
  visible?: boolean;
  onTogglePlay: () => void;
  onToggleMute?: () => void;
  onChangeVolume?: (volume: number) => void;
  onSeek: (time: number) => void;
  onExtractImage?: (nodeId: string, image: string) => void;
}> = memo(({
  nodeId = '',
  isPlaying,
  progress,
  duration,
  volume = 1,
  isMuted = false,
  hasAudioTrack = false,
  visible = true,
  onTogglePlay,
  onToggleMute = () => {},
  onChangeVolume = () => {},
  onSeek,
  onExtractImage,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isVolumeDragging, setIsVolumeDragging] = useState(false);

  const seekFromEvent = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  }, [duration, onSeek]);

  const handleBarMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    seekFromEvent(e);
  }, [seekFromEvent]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      seekFromEvent(e);
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, seekFromEvent]);

  const volumeFromEvent = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!volumeRef.current) return;
    const rect = volumeRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (rect.bottom - e.clientY) / rect.height));
    onChangeVolume(ratio);
  }, [onChangeVolume]);

  const handleVolumeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsVolumeDragging(true);
    volumeFromEvent(e);
  }, [volumeFromEvent]);

  useEffect(() => {
    if (!isVolumeDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      volumeFromEvent(e);
    };
    const handleMouseUp = () => setIsVolumeDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isVolumeDragging, volumeFromEvent]);

  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;
  const volumePercent = volume * 100;

  const handleExtractFrame = useCallback((type: 'first' | 'last' | 'current') => {
    const video = document.querySelector(`[data-video-node="${nodeId}"]`) as HTMLVideoElement | null;
    if (!video) {
      toast.error('未找到视频元素');
      return;
    }

    let time: number | undefined;
    if (type === 'first') time = 0;
    else if (type === 'last') time = video.duration || 0;

    const dataUrl = extractVideoFrame(video, time);
    if (!dataUrl) {
      toast.error('截取失败，可能是跨域视频限制');
      return;
    }

    if (onExtractImage) {
      onExtractImage(nodeId, dataUrl);
      toast.success('已截取并引用为参考');
    } else {
      toast.info('截取成功，但未配置引用回调');
    }
  }, [nodeId, onExtractImage]);

  return (
    <div
      className={`absolute bottom-0 left-0 right-0 z-20 flex items-center gap-4 px-4 py-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent nodrag nowheel nopan transition-opacity duration-200 ${visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePlay(); }}
        className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-colors"
      >
        {isPlaying ? (
          <span className="text-lg flex items-center justify-center" style={{ height: 24, transform: 'translate(0px, -3px)' }}>⏸</span>
        ) : (
          <span className="text-lg flex items-center justify-center" style={{ height: 24, transform: 'translate(0px, -3px)' }}>▶</span>
        )}
      </button>

      <span className="flex-shrink-0 text-sm text-white/90 font-mono tabular-nums min-w-[44px]">
        {formatTime(progress)}
      </span>

      <div
        ref={barRef}
        className="flex-1 h-5 flex items-center cursor-pointer relative group/bar"
        onMouseDown={handleBarMouseDown}
      >
        <div className="w-full h-1.5 bg-white/30 rounded-full relative">
          <div
            className="h-full bg-white rounded-full relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow transition-opacity opacity-0 group-hover/bar:opacity-100"
              style={isDragging ? { opacity: 1 } : undefined}
            />
          </div>
        </div>
      </div>

      <span className="flex-shrink-0 text-sm text-white/90 font-mono tabular-nums min-w-[44px] text-right">
        {formatTime(duration)}
      </span>

      {hasAudioTrack && (
        <div className="relative group/volume">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-colors"
          >
            {isMuted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-10 h-4 -mb-1 pointer-events-none group-hover/volume:pointer-events-auto" aria-hidden="true" />
          <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 transition-all duration-200 ${isVolumeDragging ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none group-hover/volume:opacity-100 group-hover/volume:translate-y-0 group-hover/volume:pointer-events-auto'}`}>
            <div className="bg-[#262626]/80 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl p-1.5 pb-2">
              <div className="text-center text-xs text-white/90 mb-1 font-mono tabular-nums">
                {isMuted ? 0 : Math.round(volume * 100)}
              </div>
              <div
                ref={volumeRef}
                className="w-5 h-24 cursor-pointer relative"
                onMouseDown={handleVolumeMouseDown}
              >
                <div className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 bg-white/30 rounded-full" />
                <div
                  className="absolute left-1/2 bottom-0 w-1 -translate-x-1/2 bg-white rounded-full"
                  style={{ height: isMuted ? '0%' : `${volumePercent}%` }}
                />
                <div
                  className="absolute left-1/2 w-3 h-3 bg-white rounded-full shadow -translate-x-1/2"
                  style={{ bottom: isMuted ? '-6px' : `calc(${volumePercent}% - 6px)` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative group/camera">
        <button
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-colors"
        >
          <Camera size={16} />
        </button>
        <div className="absolute bottom-full right-0 w-10 h-4 -mb-1 pointer-events-none group-hover/camera:pointer-events-auto" aria-hidden="true" />
        <div className="absolute bottom-full right-0 mb-2 opacity-0 translate-y-1 pointer-events-none group-hover/camera:opacity-100 group-hover/camera:translate-y-0 group-hover/camera:pointer-events-auto transition-all duration-200">
          <div className="bg-[#262626]/90 backdrop-blur-sm border border-white/10 rounded-xl shadow-xl p-1 min-w-[96px]">
            <button
              onClick={(e) => { e.stopPropagation(); handleExtractFrame('first'); }}
              className="w-full px-3 py-2 text-xs text-left text-white rounded-lg hover:bg-white/15 transition-colors"
            >
              截取首帧
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleExtractFrame('last'); }}
              className="w-full px-3 py-2 text-xs text-left text-white rounded-lg hover:bg-white/15 transition-colors"
            >
              截取尾帧
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleExtractFrame('current'); }}
              className="w-full px-3 py-2 text-xs text-left text-white rounded-lg hover:bg-white/15 transition-colors"
            >
              截取当前帧
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
